import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Check, Shuffle } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import API_BASE_URL from '../config/api';

interface AnswerChoice {
  choice_letter: string;
  choice_text: string;
  is_correct: boolean;
}

interface Question {
  id: number;
  question_number: number;
  number_of_answers: number;
  question_text?: string;
  points?: number;
  answer_choices?: AnswerChoice[];
  correct_answer?: number;
  part_id?: number;
}

interface part {
  id: number;
  titre: string;
  ordre: number;
}

interface Test {
  id: number;
  title: string;
  total_questions: number;
  type?: string;
  parts?: part[];
}

const AnswerTest: React.FC = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { testId } = useParams<{ testId: string }>();
  const [test, setTest] = useState<Test | null>(null);
  const [questions, setQuestions] = useState<Question[]>([]);
  const [parts, setparts] = useState<part[]>([]);
  const [currentpartIndex, setCurrentpartIndex] = useState<number>(0);
  const [loading, setLoading] = useState<boolean>(true);
  const [saving, setSaving] = useState<boolean>(false);
  const [userAccess, setUserAccess] = useState<number | null>(null);

  // Couleur unique pour tous les boutons de réponses sélectionnés (vert)
  const selectedAnswerColor = 'bg-green-500 hover:bg-green-600';

  // Convertir un nombre en lettre (1 -> A, 2 -> B, etc.)
  const numberToLetter = (num: number): string => {
    return String.fromCharCode(64 + num); // 65 = A
  };

  useEffect(() => {
    fetchTestAndQuestions();
    fetchUserAccess();
  }, [testId]);

  const fetchUserAccess = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    try {
      const response = await fetch(`${API_BASE_URL}/api/auth/user`, {
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      if (response.ok) {
        const data = await response.json();
        setUserAccess(data.access !== undefined ? data.access : null);
      }
    } catch (error) {
      console.error('Erreur récupération accès:', error);
    }
  };

  const fetchTestAndQuestions = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${testId}`, {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error(t('tests.answer.fetchError'));
      }

      const data = await response.json();
      setTest(data.test);
      setQuestions(data.test.questions);
      setparts(data.test.parts || []);
      setLoading(false);

    } catch (error) {
      console.error('Erreur:', error);
      setLoading(false);
    }
  };

  const handleAnswerSelect = async (questionId: number, answerNumber: number): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) return;

    setSaving(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/save-answer`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questionId,
          correctAnswer: answerNumber
        })
      });

      if (!response.ok) {
        throw new Error(t('tests.answer.saveError'));
      }

      // Mettre à jour l'état local
      setQuestions(prev =>
        prev.map(q =>
          q.id === questionId
            ? { ...q, correct_answer: answerNumber }
            : q
        )
      );

    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setSaving(false);
    }
  };

  // Handler pour les tests détaillés avec checkboxes (réponses multiples)
  const handleToggleCorrectAnswer = async (questionId: number, letter: string, isCorrect: boolean): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) return;

    setSaving(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/update-choice-correctness`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          questionId,
          letter,
          isCorrect
        })
      });

      if (!response.ok) {
        throw new Error(t('tests.answer.saveError'));
      }

      // Mettre à jour l'état local
      setQuestions(prev =>
        prev.map(q => {
          if (q.id === questionId && q.answer_choices) {
            return {
              ...q,
              answer_choices: q.answer_choices.map(c =>
                c.choice_letter === letter
                  ? { ...c, is_correct: isCorrect }
                  : c
              )
            };
          }
          return q;
        })
      );

    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setSaving(false);
    }
  };

  const handleValidate = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');

    if (!token) {
      navigate('/login');
      return;
    }

    setSaving(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/tests/${testId}/validate-answers`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || t('tests.answer.validateError'));
      }

      navigate('/my-tests');

    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setSaving(false);
    }
  };

  // Filtrer les questions par part
  const currentpart = parts[currentpartIndex];
  const currentpartQuestions = currentpart
    ? questions.filter(q => q.part_id === currentpart.id)
    : questions; // Si pas de parts, afficher toutes les questions

  // Vérifier si toutes les questions de la part courante ont une réponse
  const currentpartAnswersFilled = currentpartQuestions.every(q => q.correct_answer !== null && q.correct_answer !== undefined);

  // Vérifier si toutes les questions du test ont une réponse
  const allAnswersFilled = questions.every(q => q.correct_answer !== null && q.correct_answer !== undefined);

  // Vérifier s'il y a une part suivante
  const hasNextpart = currentpartIndex < parts.length - 1;

  // Déterminer si on peut valider (dernière part ou pas de parts)
  const canValidate = (!currentpart || !hasNextpart) && allAnswersFilled;

  const handleNextpart = (): void => {
    if (hasNextpart) {
      setCurrentpartIndex(prev => prev + 1);
      window.scrollTo(0, 0); // Remonter en haut de la page
    }
  };

  const handlePreviouspart = (): void => {
    if (currentpartIndex > 0) {
      setCurrentpartIndex(prev => prev - 1);
      window.scrollTo(0, 0);
    }
  };

  const handleBack = (): void => {
    navigate('/my-tests');
  };

  const handleRandomFill = async (): Promise<void> => {
    const token = localStorage.getItem('authToken');
    if (!token) return;

    setSaving(true);

    try {
      // Générer des réponses aléatoires pour toutes les questions
      const randomAnswers = questions.map(q => ({
        questionId: q.id,
        correctAnswer: Math.floor(Math.random() * q.number_of_answers) + 1
      }));

      // Sauvegarder chaque réponse
      for (const answer of randomAnswers) {
        await fetch(`${API_BASE_URL}/api/tests/save-answer`, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(answer)
        });
      }

      // Mettre à jour l'état local
      setQuestions(prev =>
        prev.map(q => {
          const randomAnswer = randomAnswers.find(a => a.questionId === q.id);
          return randomAnswer ? { ...q, correct_answer: randomAnswer.correctAnswer } : q;
        })
      );

    } catch (error) {
      console.error('Erreur:', error);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('tests.answer.loadingTest')}</div>
      </div>
    );
  }

  if (!test) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800 flex items-center justify-center">
        <div className="text-white text-xl">{t('tests.answer.testNotFound')}</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-600 to-blue-800">
      {/* Header */}
      <header className="bg-white shadow-md">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <button
              onClick={handleBack}
              className="text-gray-600 hover:text-gray-800 transition"
            >
              <ArrowLeft className="w-6 h-6" />
            </button>
            <h1 className="text-2xl font-bold text-gray-800">
              {t('tests.answer.pageTitle', { testTitle: test.title })}
            </h1>
          </div>
          {userAccess === 1 && (
            <button
              onClick={handleRandomFill}
              disabled={saving}
              className="flex items-center gap-2 bg-purple-600 hover:bg-purple-700 text-white font-semibold py-2 px-4 rounded-lg transition disabled:opacity-50"
            >
              <Shuffle className="w-5 h-5" />
              Aléatoire
            </button>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="max-w-7xl mx-auto px-6 py-12">
        <div className="bg-white rounded-lg shadow-2xl p-8">
          <div className="mb-6">
            {currentpart ? (
              <>
                <div className="flex items-center justify-between mb-4">
                  <h2 className="text-2xl font-bold text-gray-800">
                    {currentpart.titre}
                  </h2>
                  <span className="text-sm text-gray-600 bg-gray-100 px-4 py-2 rounded-lg">
                    {t('tests.answer.partProgress', { current: currentpartIndex + 1, total: parts.length })}
                  </span>
                </div>
                <p className="text-gray-600">
                  {t('tests.answer.partInstruction')}
                </p>
              </>
            ) : (
              <>
                <h2 className="text-2xl font-bold text-gray-800 mb-2">
                  {t('tests.answer.selectCorrectAnswer')}
                </h2>
                <p className="text-gray-600">
                  {t('tests.answer.autoSaveInfo')}
                </p>
              </>
            )}
          </div>

          {/* Vue pour tests détaillés */}
          {test.type === 'detailed' && (
            <div className="space-y-4">
              {currentpartQuestions.map((q) => (
                <div
                  key={q.id}
                  className="border-2 border-gray-300 rounded-lg p-4 bg-white"
                >
                  {/* Question title */}
                  <div className="mb-4">
                    <h3 className="font-bold text-lg text-gray-800 mb-1">
                      Question {q.question_number}
                    </h3>
                    {q.question_text && (
                      <p className="text-gray-700">{q.question_text}</p>
                    )}
                    {q.points && q.points > 1 && (
                      <p className="text-sm text-gray-600 mt-1">
                        ({q.points} points)
                      </p>
                    )}
                  </div>

                  {/* Answer choices avec checkboxes */}
                  {q.answer_choices && q.answer_choices.length > 0 && (
                    <div className="space-y-3">
                      <p className="text-sm font-medium text-gray-700 mb-2">
                        Marquer la/les bonne(s) réponse(s) :
                      </p>
                      {q.answer_choices.map((choice) => (
                        <div key={choice.choice_letter} className="flex items-start gap-3">
                          <input
                            type="checkbox"
                            checked={choice.is_correct}
                            onChange={(e) => handleToggleCorrectAnswer(q.id, choice.choice_letter, e.target.checked)}
                            disabled={saving}
                            className="mt-1 w-5 h-5 text-blue-600 disabled:opacity-50"
                          />
                          <div className="flex-1">
                            <label className={`text-sm ${choice.is_correct ? 'font-semibold text-green-700' : 'text-gray-700'}`}>
                              <span className="font-bold text-blue-600 mr-2">
                                {choice.choice_letter}.
                              </span>
                              {choice.choice_text}
                              {choice.is_correct && (
                                <Check className="inline-block ml-2 w-4 h-4 text-green-600" />
                              )}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          {/* Tableau des questions - Tests simples uniquement */}
          {test.type !== 'detailed' && (
          <div className="overflow-x-auto">
            <table className="w-full border-collapse">
              <thead>
                <tr className="bg-gray-800">
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('tests.answer.questionLabel')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('tests.answer.answerLabel')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('tests.answer.questionLabel')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('tests.answer.answerLabel')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('tests.answer.questionLabel')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('tests.answer.answerLabel')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('tests.answer.questionLabel')}
                  </th>
                  <th className="border border-gray-600 px-2 py-2 text-center font-semibold text-white text-sm">
                    {t('tests.answer.answerLabel')}
                  </th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  // Diviser les questions en 4 groupes égaux
                  const quarterSize = Math.ceil(currentpartQuestions.length / 4);
                  const group1 = currentpartQuestions.slice(0, quarterSize);
                  const group2 = currentpartQuestions.slice(quarterSize, quarterSize * 2);
                  const group3 = currentpartQuestions.slice(quarterSize * 2, quarterSize * 3);
                  const group4 = currentpartQuestions.slice(quarterSize * 3);

                  // Nombre de lignes = taille du plus grand groupe
                  const maxRows = Math.max(group1.length, group2.length, group3.length, group4.length);

                  return Array.from({ length: maxRows }).map((_, rowIndex) => {
                    const q1 = group1[rowIndex];
                    const q2 = group2[rowIndex];
                    const q3 = group3[rowIndex];
                    const q4 = group4[rowIndex];

                    const renderQuestion = (q: typeof q1) => {
                      if (!q) {
                        return (
                          <>
                            <td className="border border-gray-300 px-2 py-2"></td>
                            <td className="border border-gray-300 px-2 py-2"></td>
                          </>
                        );
                      }

                      return (
                        <>
                          <td className="border border-gray-300 px-2 py-2 text-center text-gray-700 font-semibold text-sm">
                            {t('tests.answer.questionNumber', { number: q.question_number })}
                          </td>
                          <td className="border border-gray-300 px-2 py-2">
                            <div className="flex gap-1 justify-center flex-wrap">
                              {Array.from({ length: q.number_of_answers }).map((_, answerIndex) => {
                                const answerNumber = answerIndex + 1;
                                const answerLetter = numberToLetter(answerNumber);
                                const isSelected = q.correct_answer === answerNumber;
                                const colorClass = isSelected ? selectedAnswerColor : 'bg-gray-300 hover:bg-gray-400';

                                return (
                                  <button
                                    key={answerNumber}
                                    onClick={() => handleAnswerSelect(q.id, answerNumber)}
                                    disabled={saving}
                                    className={`
                                      w-8 h-8 rounded-lg font-bold transition text-sm
                                      ${colorClass}
                                      ${isSelected ? 'text-white' : 'text-gray-700'}
                                      ${saving ? 'opacity-50 cursor-not-allowed' : ''}
                                    `}
                                  >
                                    {answerLetter}
                                  </button>
                                );
                              })}
                            </div>
                          </td>
                        </>
                      );
                    };

                    return (
                      <tr
                        key={rowIndex}
                        className={`${rowIndex % 2 === 0 ? 'bg-white' : 'bg-gray-100'} hover:bg-blue-50`}
                      >
                        {renderQuestion(q1)}
                        {renderQuestion(q2)}
                        {renderQuestion(q3)}
                        {renderQuestion(q4)}
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
          )}

          {/* Boutons de navigation */}
          <div className="flex justify-between items-center mt-8">
            {/* Bouton Précédent */}
            {currentpartIndex > 0 && (
              <button
                onClick={handlePreviouspart}
                className="flex items-center gap-2 bg-gray-600 hover:bg-gray-700 text-white font-semibold py-3 px-8 rounded-lg transition"
              >
                <ArrowLeft className="w-5 h-5" />
                {t('tests.answer.previousPart')}
              </button>
            )}

            <div className="flex-1"></div>

            {/* Bouton Suivant ou Valider */}
            {hasNextpart ? (
              <button
                onClick={handleNextpart}
                disabled={!currentpartAnswersFilled}
                className={`flex items-center gap-2 font-semibold py-3 px-8 rounded-lg transition ${
                  currentpartAnswersFilled
                    ? 'bg-blue-600 hover:bg-blue-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                {t('tests.answer.nextPart')}
                <ArrowLeft className="w-5 h-5 rotate-180" />
              </button>
            ) : (
              <button
                onClick={handleValidate}
                disabled={!canValidate}
                className={`flex items-center gap-2 font-semibold py-3 px-8 rounded-lg transition ${
                  canValidate
                    ? 'bg-green-600 hover:bg-green-700 text-white cursor-pointer'
                    : 'bg-gray-300 text-gray-500 cursor-not-allowed'
                }`}
              >
                <Check className="w-5 h-5" />
                {t('tests.answer.validateAll')}
              </button>
            )}
          </div>
        </div>
      </main>
    </div>
  );
};

export default AnswerTest;
